"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { ChevronDown, Check, Lock, X } from "lucide-react";

import { cn } from "@/lib/utils";

interface SingleSelectProps {
  selected: {
    label: string;
    value: string;
    icon?: React.ReactNode;
  };
  options: {
    label: string;
    value: string;
    disabled?: boolean;
    locked?: boolean;
    icon?: React.ReactNode;
  }[];
  onSelect: (value: string) => void;
  label?: string;
  className?: string;
}

const SingleSelect: React.FC<SingleSelectProps> = ({
  selected,
  options,
  label,
  onSelect,
  className,
}) => {
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [tooltip, setTooltip] = useState<string | null>(null);

  const toggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  const handleUpgrade = () => {
    router.push("/subscribe");
    setTooltip(null);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={cn("tw:w-full tw:flex tw:flex-col tw:gap-2.5", className)}
      ref={containerRef}
    >
      {label && <p className="tw:text-xxs">{label}</p>}
      <div className="tw:relative tw:w-full" ref={selectRef}>
        <div
          className={cn(
            "tw:w-full tw:flex tw:items-center tw:justify-between tw:px-2 tw:h-[30px] tw:rounded-sm tw:shadow-[0px_0px_2px_0px_rgba(0,0,0,0.1)] tw:border tw:border-neutral-200",
            className
          )}
          onClick={toggleExpanded}
        >
          <div className="tw:flex tw:items-center tw:gap-1.5">
            {selected.icon && selected.icon}
            <span className="tw:text-xs tw:whitespace-nowrap tw:truncate">
              {selected.label}
            </span>
          </div>
          <ChevronDown
            size={16}
            className={cn(
              "tw:text-neutral-500 tw:transition-all tw:ease-in-out tw:duration-150",
              expanded && "tw:rotate-180"
            )}
          />
        </div>
        {expanded && (
          <div className="tw:absolute tw:top-full tw:left-0 tw:flex tw:flex-col tw:gap-1 tw:w-full tw:max-h-[150px] tw:py-1.5 tw:px-1 tw:overflow-y-auto tw:bg-white tw:shadow-lg tw:border tw:border-neutral-200 tw:rounded-sm tw:mt-1 tw:z-[1000]">
            {options.map((option) => {
              const isSelected = selected.value === option.value;
              const isDisabled = option.disabled || option.locked;

              return (
                <div
                  key={option.value}
                  className={cn(
                    "tw:relative tw:w-full tw:p-1 tw:flex tw:items-center tw:justify-between tw:gap-1 tw:rounded-lg",
                    isSelected && "tw:bg-gray-100",
                    option.disabled && "tw:text-neutral-400",
                    option.locked && "tw:text-neutral-400"
                  )}
                  onClick={() => {
                    if (option.locked) {
                      setTooltip(option.label);
                      return;
                    }

                    if (!isDisabled) {
                      onSelect(option.value);
                      setExpanded(false);
                    }
                  }}
                >
                  <div
                    className={cn(
                      "tw:flex tw:items-center tw:gap-1.5 tw:flex-1",
                      isDisabled && "tw:opacity-30"
                    )}
                  >
                    {option.icon && option.icon}
                    <span className="tw:text-xs tw:whitespace-nowrap tw:truncate">
                      {option.label}
                    </span>
                  </div>
                  {isSelected && (
                    <Check size={16} className="tw:flex-shrink-0" />
                  )}
                  {option.locked && (
                    <div className="tw:flex-shrink-0">
                      <Lock size={14} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {tooltip && (
        <div className="tw:fixed tw:top-1/2 tw:left-1/2 tw:transform tw:-translate-x-1/2 tw:-translate-y-1/2 tw:flex tw:flex-col tw:items-center tw:gap-2 tw:min-w-[200px] tw:bg-white tw:shadow-lg tw:rounded-lg tw:pb-3 tw:px-3 tw:pt-5 tw:z-[1400] tw:animate-[fadeeIn_0.2s_ease] tw:border tw:border-gray-200">
          <button
            className="tw:absolute tw:top-1 tw:right-1 tw:w-6 tw:h-5 tw:rounded-full tw:border-none tw:grid tw:place-items-center tw:bg-neutral-100 tw:text-gray-500"
            onClick={() => setTooltip(null)}
          >
            <X size={14} />
          </button>
          <p className="tw:m-0 tw:text-sm tw:text-center tw:text-slate-900">
            To unlock {tooltip}
          </p>
          <button
            className="tw:py-1.5 tw:px-4 tw:bg-primary-red tw:text-white tw:border-none tw:rounded-lg tw:font-medium tw:text-sm"
            onClick={handleUpgrade}
          >
            Upgrade
          </button>
        </div>
      )}
    </div>
  );
};

export default SingleSelect;
